import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { User, Package, Heart, MapPin, Settings, LogOut, ChevronRight, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function UserDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login');
      } else if (user.role === 'admin') {
        navigate('/admin');
      }
    }
  }, [user, navigate, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { icon: User, label: 'Profile', path: '/dashboard' },
    { icon: Package, label: 'My Orders', path: '/dashboard/orders' },
    { icon: Heart, label: 'Wishlist', path: '/dashboard/wishlist' },
    { icon: MapPin, label: 'Addresses', path: '/dashboard/addresses' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-surface" data-testid="user-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-between w-full bg-white rounded-2xl p-4 mb-4"
            >
              <span className="font-body font-medium text-secondary">Menu</span>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Sidebar Content */}
            <div className={`lg:block ${mobileMenuOpen ? 'block' : 'hidden'}`}>
              <div className="bg-white rounded-3xl p-6 mb-4">
                {/* User Info */}
                <div className="flex items-center gap-4 pb-6 border-b border-gray-100">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={user.name}
                      className="w-14 h-14 rounded-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=E0E7FF&color=0071E3`;
                      }}
                    />
                  ) : (
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-7 h-7 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-body font-semibold text-secondary truncate">{user.name}</p>
                    <p className="text-sm text-muted-foreground font-body truncate">{user.email}</p>
                  </div>
                </div>

                {/* Menu Items */}
                <nav className="pt-4 space-y-1">
                  {menuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all font-body ${isActive(item.path)
                        ? 'bg-primary/10 text-primary'
                        : 'text-secondary hover:bg-surface'
                        }`}
                      data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </div>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  ))}

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-destructive hover:bg-destructive/10 transition-all font-body"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

// Profile Page Component
export function ProfilePage() {
  const { user } = useAuth();

  if (!user) return null;

  return (
    <div className="bg-white rounded-3xl p-6 md:p-8" data-testid="profile-page">
      <h1 className="font-heading text-2xl md:text-3xl text-secondary mb-6">My Profile</h1>

      <div className="space-y-6">
        <div className="flex items-center gap-6">
          {user.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-24 h-24 rounded-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || 'User')}&background=E0E7FF&color=0071E3`;
              }}
            />
          ) : (
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-primary" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="font-heading text-xl text-secondary truncate">{user.name}</h2>
            <p className="text-muted-foreground font-body truncate">{user.email}</p>
            {user.role === 'admin' && (
              <span className="inline-block mt-2 bg-primary/10 text-primary text-xs px-3 py-1 rounded-full font-body">
                Admin
              </span>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 pt-6 border-t border-gray-100">
          <div>
            <label className="text-sm text-muted-foreground font-body">Full Name</label>
            <p className="font-body text-secondary mt-1">{user.name}</p>
          </div>
          <div className="min-w-0">
            <label className="text-sm text-muted-foreground font-body">Email Address</label>
            <p className="font-body text-secondary mt-1 break-all">{user.email}</p>
          </div>
          <div>
            <label className="text-sm text-muted-foreground font-body">Account Type</label>
            <p className="font-body text-secondary mt-1 capitalize">{user.role || 'User'}</p>
          </div>
          <div>
            <label className="text-sm text-muted-foreground font-body">Member Since</label>
            <p className="font-body text-secondary mt-1">
              {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
